// src/pages/TermsPage.jsx
import React from 'react';
import { Box, Heading, Text, VStack, Link, List, ListItem } from '@chakra-ui/react';

export default function TermsPage() {
  return (
    <Box maxW="900px" mx="auto" p={{ base: 6, md: 10 }}>
      <VStack align="start" spacing={6}>
        <Heading as="h1" size="lg">
          Fixihub Terms, Privacy, Cookies & Data Protection Policy
        </Heading>
        <Text fontSize="sm" color="gray.600">
          Version: 01-09-2025
        </Text>

        {/* ---------- Terms of Service ---------- */}
        <Heading as="h2" size="lg">
          1. Terms of Service
        </Heading>

        <Heading as="h3" size="md">
          1.1 Who we are
        </Heading>
        <Text>
          Fixihub is an online software platform. We provide a neutral venue where home-improvement
          companies can manage leads, quotes, and sales.
        </Text>

        <Heading as="h3" size="md">
          1.2 Free use and “as is” notice
        </Heading>
        <Text>
          Fixihub is currently <b>free to use</b>. It is provided “as is” and “as available.”
        </Text>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>
            We do not guarantee the service will be error-free, uninterrupted, or fit your specific
            needs.
          </ListItem>
          <ListItem>
            You are responsible for how you use Fixihub and for keeping your own backups.
          </ListItem>
          <ListItem>
            We may change, suspend, or stop the service at any time without notice.
          </ListItem>
          <ListItem>Any third-party links or integrations are used at your own risk.</ListItem>
        </List>

        <Heading as="h3" size="md">
          1.3 Your responsibilities
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>Provide accurate information when signing up.</ListItem>
          <ListItem>Keep your login details secure.</ListItem>
          <ListItem>
            Use Fixihub in compliance with applicable laws (including data protection law).
          </ListItem>
          <ListItem>
            Understand that Fixihub is only a venue — you remain responsible for your customer data
            and legal obligations.
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          1.4 Acceptable use
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>No unlawful, offensive, or misleading content.</ListItem>
          <ListItem>No attempts to hack, overload, or interfere with Fixihub.</ListItem>
          <ListItem>No copying, reselling, or exploiting the service without permission.</ListItem>
        </List>

        <Heading as="h3" size="md">
          1.5 Intellectual property
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>You own any customer data you input.</ListItem>
          <ListItem>We own the Fixihub software and brand.</ListItem>
          <ListItem>
            You grant us permission to process your data only as needed to operate the platform.
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          1.6 Liability
        </Heading>
        <Text>To the fullest extent allowed by law:</Text>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>
            Fixihub and its team are not liable for any loss of data, profits, or business.
          </ListItem>
          <ListItem>
            We are not liable for indirect or consequential damages from using (or not being able to
            use) the service, even if warned.
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          1.7 Termination
        </Heading>
        <Text>
          You may stop using Fixihub at any time. We may suspend or close accounts that break these
          terms.
        </Text>

        {/* ---------- Privacy ---------- */}
        <Heading as="h2" size="lg" id="privacy">
          2. Privacy & Neutral Role
        </Heading>

        <Heading as="h3" size="md">
          2.1 Neutral platform
        </Heading>
        <Text>
          Fixihub acts as a <b>neutral venue</b>. We do not decide what customer information is
          collected or how it is used.
        </Text>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>
            Your organisation (the company using Fixihub) is the <b>data controller</b> for any
            personal data you input.
          </ListItem>
          <ListItem>
            We act only as a <b>technical provider</b> to store and display the information you
            choose to enter.
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          2.2 What we collect directly
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>Account details (your name, email, company).</ListItem>
          <ListItem>Basic technical logs (IP address, device info, error reports).</ListItem>
        </List>

        <Heading as="h3" size="md">
          2.3 How we use this
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>To create and secure your Fixihub account.</ListItem>
          <ListItem>To operate the platform.</ListItem>
          <ListItem>To improve reliability and security.</ListItem>
        </List>

        <Heading as="h3" size="md">
          2.4 Sharing
        </Heading>
        <Text>
          We do not sell your data. Fixihub is built on Google Firebase, which acts as our cloud
          provider.
        </Text>

        <Heading as="h3" size="md">
          2.5 Retention
        </Heading>
        <Text>
          We keep only what is necessary to operate your account. If you delete your account, we
          remove personal login details within a reasonable timeframe.
        </Text>

        <Heading as="h3" size="md">
          2.6 Your rights
        </Heading>
        <Text>
          If you are an end-customer of a company using Fixihub, please contact that company (not
          us) to exercise your GDPR rights. If you are a direct Fixihub account holder, contact us
          at <Link href="mailto:developers@fixihub.pro">developers@fixihub.pro</Link> for any
          account-related requests.
        </Text>

        {/* ---------- Cookies ---------- */}
        <Heading as="h2" size="lg">
          3. Cookies Policy
        </Heading>

        <Heading as="h3" size="md">
          3.1 What are cookies?
        </Heading>
        <Text>Cookies are small files stored on your device to make Fixihub work properly.</Text>

        <Heading as="h3" size="md">
          3.2 Types we use
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>
            <b>Essential cookies</b> – required for login and navigation.
          </ListItem>
          <ListItem>
            <b>Preference cookies</b> – e.g. to remember settings.
          </ListItem>
          <ListItem>
            <b>Analytics cookies</b> – only if we add them in future (not by default).
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          3.3 Control
        </Heading>
        <Text>
          You can block or delete cookies in your browser settings, but some parts of Fixihub may
          not work without them.
        </Text>

        {/* ---------- Data Protection ---------- */}
        <Heading as="h2" size="lg" id="data-protection">
          4. Data Protection
        </Heading>

        <Heading as="h3" size="md">
          4.1 Hosting and security
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>Fixihub is hosted on Google Firebase in EU/UK regions.</ListItem>
          <ListItem>Data is encrypted in transit (TLS) and at rest.</ListItem>
          <ListItem>Access is role-based and logged.</ListItem>
        </List>

        <Heading as="h3" size="md">
          4.2 Neutral responsibility
        </Heading>
        <List spacing={2} styleType="disc" pl={6}>
          <ListItem>Organisations using Fixihub remain the controllers of customer data.</ListItem>
          <ListItem>
            We provide the technical safeguards but do not decide how customer data is collected,
            used, or shared.
          </ListItem>
        </List>

        <Heading as="h3" size="md">
          4.3 Breach handling
        </Heading>
        <Text>
          If Fixihub becomes aware of a security issue affecting stored data, we will notify
          affected account holders. Each organisation is responsible for informing its own customers
          if required by law.
        </Text>

        {/* ---------- Changes ---------- */}
        <Heading as="h2" size="lg">
          5. Changes
        </Heading>
        <Text>We may update this document from time to time. Enjoy Fixihub :)</Text>

        <Text fontSize="sm" color="gray.600" pt={6}>
        </Text>
      </VStack>
    </Box>
  );
}
